import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

interface Department {
  id: string;
  name: string;
}

interface TrainingVideo {
  id: string;
  title: string;
  description: string | null;
  category: string | null;
  is_mandatory: boolean;
  mandatory_for_departments: string[];
}

interface TrainingVideoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  video: TrainingVideo | null;
  onSuccess: () => void;
}

export function TrainingVideoEditDialog({
  open,
  onOpenChange,
  video,
  onSuccess,
}: TrainingVideoEditDialogProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [isMandatory, setIsMandatory] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && video) {
      setTitle(video.title);
      setDescription(video.description || "");
      setCategory(video.category || "");
      setIsMandatory(video.is_mandatory);
      setSelectedDepartments(video.mandatory_for_departments || []);
      loadDepartments();
    }
  }, [open, video]);

  const loadDepartments = async () => {
    const { data } = await supabase
      .from('departments')
      .select('id, name')
      .order('sort_order');
    setDepartments(data || []);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !video) {
      toast.error("Bitte geben Sie einen Titel ein");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('training_videos')
        .update({
          title: title.trim(),
          description: description.trim() || null,
          category: category.trim() || null,
          is_mandatory: isMandatory,
          mandatory_for_departments: isMandatory ? selectedDepartments : [],
        })
        .eq('id', video.id);

      if (error) throw error;

      toast.success("Video erfolgreich aktualisiert!");
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Error updating video:', error);
      toast.error("Fehler beim Aktualisieren des Videos");
    } finally {
      setSaving(false);
    }
  };

  const toggleDepartment = (deptId: string) => {
    setSelectedDepartments(prev =>
      prev.includes(deptId)
        ? prev.filter(id => id !== deptId)
        : [...prev, deptId]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lernvideo bearbeiten</DialogTitle>
          <DialogDescription>
            Bearbeiten Sie die Metadaten des Schulungsvideos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-title">Titel *</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Einführung Messgerät XY"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Beschreibung</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optionale Beschreibung des Inhalts..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-category">Kategorie</Label>
            <Input
              id="edit-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="z.B. Software, Messgeräte, Sicherheit"
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Switch
              id="edit-mandatory"
              checked={isMandatory}
              onCheckedChange={setIsMandatory}
            />
            <Label htmlFor="edit-mandatory" className="cursor-pointer">
              Pflicht-Video (muss von Mitarbeitern bestätigt werden)
            </Label>
          </div>

          {isMandatory && (
            <div className="space-y-2 p-3 bg-muted rounded-lg">
              <Label className="text-sm">Pflicht für Abteilungen:</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Leer lassen = für alle verpflichtend
              </p>
              <div className="grid grid-cols-2 gap-2">
                {departments.map(dept => (
                  <div key={dept.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`edit-${dept.id}`}
                      checked={selectedDepartments.includes(dept.id)}
                      onCheckedChange={() => toggleDepartment(dept.id)}
                    />
                    <Label htmlFor={`edit-${dept.id}`} className="text-sm cursor-pointer">
                      {dept.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button onClick={handleSubmit} disabled={saving || !title.trim()}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Speichern
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
